import { format } from 'date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Calendar, ChevronLeft, ChevronRight, RefreshCw, TrendingUp } from 'lucide-react';
import {
	CartesianGrid,
	ComposedChart,
	Line,
	ReferenceArea,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { ForecastTransactionsTable } from "./ForecastTransactionsTable";
import { useTerminology } from "@/hooks/useTerminology";
import { getCurrencySymbol } from "@/utils/currency";
import { useApp } from "../../contexts/AppContext";
import { usePermissions } from "../../hooks/usePermissions";
import { useBaseForecast } from "../../hooks/useBaseForecast";
import { useForecastRunTransactions } from "../../hooks/useForecastRunTransactions";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import { useUserPreferenceDefaults } from "../../contexts/UserPreferenceDefaultsContext";
import type { DateBreakdownTransaction } from "../../hooks/useDrillDown";
import {
	generateDateBreakdownDrillDown,
	useDrillDown,
} from '../../hooks/useDrillDown';
import { useFormatting } from '../../hooks/useFormatting';
import { useVisibleScenarios } from '../../hooks/useVisibleScenarios';
import { formatDate } from '../../lib/utils';
import { DrillDownModal } from '../modals/DrillDownModal';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
	BASE_ID,
	ClusterPickerPopover,
	SCENARIO_COLOURS,
	ScenarioOverlayPicker,
} from "./ScenarioOverlayPicker";
import type { ClusterCandidate } from "./ScenarioOverlayPicker";
import { useScenarioForecasts } from "../../hooks/useScenarioForecasts";
import { applyScenarioToForecast } from "../../lib/scenarioEngine";

const CHART_LINE_COLOR = 'var(--accent-sky-500)';
const CHART_THRESHOLD_COLOR = 'var(--mc-error-icon)';

type TimeRange = "7" | "30" | "90";

interface ForecastTimelineProps {
	/** Pre-seed scenario IDs as overlays. BASE_ID is always included. */
	initialScenarioIds?: string[];
	/** Hide the top metric cards (useful when embedded inside another page) */
	hideMetricCards?: boolean;
	/**
	 * Show the drill-down "View Scenario" action on scenario overlay points.
	 * Defaults to true (90-day forecast page). Set false when the timeline is
	 * already embedded inside a scenario page, where navigating to the scenario
	 * is redundant.
	 */
	enableViewScenario?: boolean;
	/**
	 * Custom handler for the drill-down "View Transactions" action. When provided
	 * the caller decides which transactions table to filter (e.g. a scenario's
	 * own forecast transactions table). When omitted, the component falls back to
	 * its built-in base-forecast transactions table rendered below the chart; if
	 * that table isn't rendered either (e.g. scenario-embed mode), the action is
	 * hidden.
	 */
	onViewTransactions?: (date: Date) => void;
}

export const ForecastTimeline: React.FC<ForecastTimelineProps> = ({
	initialScenarioIds,
	hideMetricCards = false,
	enableViewScenario = true,
	onViewTransactions,
}) => {
	const { getForecastTerm, getCashMovementTerms, getScenarioTerm } =
		useTerminology();
	// When embedded inside a scenario detail view, lock the overlay to that scenario only
	const isScenarioEmbed = !!initialScenarioIds?.length;
	// The built-in base-forecast transactions table renders below the chart only
	// on the standalone 90-day page (not when embedded in a scenario view).
	const showBaseTransactionsTable = !isScenarioEmbed;
	const { mode, currentUser, handleNavigate } = useApp();
	const { hasPermission } = usePermissions();
	const { formatCurrency } = useFormatting();
	const scenarios = useVisibleScenarios();
	const { drillDownData, isOpen, showDrillDown, closeDrillDown } =
		useDrillDown();
	// Forecast day selected via a drill-down "View Transactions" action. Drives
	// the forecast transactions table into Transactions view, filtered to the day.
	const [txnDateFilter, setTxnDateFilter] = useState<Date | null>(null);
	const { getUserPreferences } = useUserPreferences();
	const { defaults } = useUserPreferenceDefaults();
	const userPrefs = getUserPreferences(currentUser.id);

	// Resolve health zone settings: user override → admin default
	const healthyThreshold = userPrefs.healthyThreshold ?? defaults.healthyThreshold;
	const cautionThreshold = userPrefs.cautionThreshold ?? defaults.cautionThreshold;
	const minCashThreshold = defaults.minCashThreshold;
	const displayMode = userPrefs.chartHealthForecast90 ?? defaults.chartHealthForecast90;
	const showBands = displayMode === 'bands';
	const showLine = displayMode === 'bands' || displayMode === 'line';

	// Resolve default 90-day view: user override → admin default → "90"
	const resolvedDefaultView = userPrefs.defaultForecastView ?? defaults.defaultForecastView;
	const defaultTimeRange: TimeRange =
		resolvedDefaultView === '7-day' ? '7' :
		resolvedDefaultView === '30-day' ? '30' :
		'90';
	const [timeRange, setTimeRange] = useState<TimeRange>(defaultTimeRange);
	// Selected scenario IDs — BASE_ID is always the first option
	// If initialScenarioIds are provided (e.g. from scenario builder), pre-seed them
	const [selectedIds, setSelectedIds] = useState<string[]>(() => {
		if (initialScenarioIds && initialScenarioIds.length > 0) {
			return [BASE_ID, ...initialScenarioIds.filter(id => id !== BASE_ID)];
		}
		return [BASE_ID];
	});
	const {
		baseForecast: liveBaseData,
		isLoading: isLoadingForecast,
		error: forecastError,
		forecastNotReady,
	} = useBaseForecast();

	const isLive = mode === "live";
	const nonBaseIds = selectedIds.filter((id) => id !== BASE_ID);
	const { data: scenarioForecastData } = useScenarioForecasts(
		nonBaseIds,
		"90d",
	);

	// Forecast run transactions for drill-down (live mode only)
	const { forecastRunId } = useBaseForecast();
	const {
		data: forecastRunTransactions,
		error: runTxnError,
	} = useForecastRunTransactions(forecastRunId, undefined, undefined, isLive);

	const getDaysCount = () => {
		switch (timeRange) {
			case "7":
				return 7;
			case "30":
				return 30;
			case "90":
			default:
				return 90;
		}
	};

	// Build the merged chart data: one row per day with a key per selected scenario
	const getFilteredData = () => {
		const days = getDaysCount();

		// Base line data — from shared context (handles demo/live switch)
		const baseSource = liveBaseData;

		const slicedBase = baseSource.slice(0, days + 1);

		// For each selected scenario that isn't __base__, pre-compute scenario data
		const scenarioDataMap: Record<string, { balance: number }[]> = {};
		for (const id of selectedIds) {
			if (id !== BASE_ID) {
				if (isLive) {
					const forecast = scenarioForecastData[id];
					if (forecast) {
						if (forecast.metrics.changeCount === 0) {
							scenarioDataMap[id] = slicedBase.map((p) => ({ balance: p.balance }));
						} else {
							scenarioDataMap[id] = forecast.projectedForecast
								.map((p) => ({ balance: p.scenarioBalance }))
								.slice(0, days + 1);
						}
					}
				} else {
					const scenario = scenarios.find((s) => s.id === id);
					if (scenario) {
						const result = applyScenarioToForecast(
							liveBaseData,
							scenario.changes,
						);
						scenarioDataMap[id] = result.projectedForecast
							.map((p) => ({ balance: p.scenarioBalance }))
							.slice(0, days + 1);
					}
				}
			}
		}

		return slicedBase.map((point, idx) => {
			const row: Record<string, unknown> = {
				date: point.date,
				dateStr: formatDate(point.date),
				[BASE_ID]: point.balance,
			};
			for (const [id, data] of Object.entries(scenarioDataMap)) {
				row[id] = data[idx]?.balance ?? null;
			}
			return row;
		});
	};

	const filteredData = getFilteredData();

	// ── Pill scroll state ──────────────────────────────────────────────────────
	const pillsScrollRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollState = useCallback(() => {
		const el = pillsScrollRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		updateScrollState();
	});

	const scrollPills = (dir: 'left' | 'right') => {
		const el = pillsScrollRef.current;
		if (!el) return;
		el.scrollBy({ left: dir === 'right' ? 120 : -120, behavior: 'smooth' });
	};

	const toggleScenario = (id: string) => {
		if (id === BASE_ID) {
			// Base can't be deselected if it's the only one
			if (selectedIds.length === 1 && selectedIds[0] === BASE_ID) return;
			setSelectedIds((prev) =>
				prev.includes(BASE_ID)
					? prev.filter((s) => s !== BASE_ID)
					: [BASE_ID, ...prev.filter((s) => s !== BASE_ID)],
			);
		} else {
			setSelectedIds((prev) =>
				prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
			);
		}
	};

	// Map each selected ID to a colour
	const colourFor = (id: string, selectionIndex: number) =>
		id === BASE_ID
			? CHART_LINE_COLOR
			: SCENARIO_COLOURS[selectionIndex % SCENARIO_COLOURS.length];

	// Build a label for a given ID — for chart tooltips and legends
	const labelFor = (id: string) => {
		if (id === BASE_ID) return `Base ${getForecastTerm(true)}`;
		const sc = scenarios.find((s) => s.id === id);
		if (!sc) return id;
		return `${sc.name} (${sc.status})`;
	};

	// ── Cluster picker state ─────────────────────────────────────────────────
	type ClusterStateType = {
		candidates: ClusterCandidate[];
		periodLabel: string;
		anchorX: number;
		anchorY: number;
		point: Record<string, unknown>;
		dataIndex: number;
	} | null;

	const [clusterState, setClusterState] = useState<ClusterStateType>(null);
	// Chart container the cluster picker is anchored to, so it scrolls with the page.
	const chartContainerRef = useRef<HTMLDivElement>(null);
	// Keep a ref so handleChartMouseMove always reads the live value (no stale closure)
	const clusterStateRef = useRef<ClusterStateType>(null);
	const setCluster = (val: ClusterStateType) => {
		clusterStateRef.current = val;
		setClusterState(val);
	};

	// Recharts v3: track the hovered index via onMouseMove and read on click.
	const activeIdxRef = useRef<number | null>(null);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChartMouseMove = (state: any) => {
		const raw = state?.activeTooltipIndex ?? state?.activeIndex;
		const idx = raw != null ? Number(raw) : null;
		activeIdxRef.current = idx;
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handlePointClick = (state: any, event?: React.MouseEvent) => {
		const idx = activeIdxRef.current;
		if (idx == null || Number.isNaN(idx) || !filteredData[idx]) return;
		const point = filteredData[idx];

		// Build candidate list from every selected series that has a value at this point
		const candidates: ClusterCandidate[] = selectedIds
			.filter((id) => typeof point[id] === "number")
			.map((id, selIdx) => ({
				id,
				label: labelFor(id),
				value: point[id] as number,
				colour: colourFor(
					id,
					selectedIds.indexOf(id) >= 0 ? selectedIds.indexOf(id) : selIdx,
				),
			}));

		if (candidates.length === 0) return;

		// Single series → fire directly, no picker needed
		if (candidates.length === 1) {
			triggerDrillDown(point, candidates[0].id);
			return;
		}

		// Multiple series → show cluster picker at click coordinates
		if (event) {
			setCluster({
				candidates,
				periodLabel: point.dateStr as string,
				anchorX: event.clientX,
				anchorY: event.clientY,
				point,
				dataIndex: idx,
			});
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const triggerDrillDown = (point: any, scenarioId: string = BASE_ID) => {
		const balance = point[scenarioId] as number;
		if (balance == null) return;

		// Resolve the base point for this chart date.
		const dayIndex = filteredData.findIndex((d) => d.date === point.date);
		const sourceForecast = liveBaseData;
		const sourcePoint = sourceForecast[dayIndex];
		let incoming = sourcePoint?.incoming ?? 0;
		let outgoing = sourcePoint?.outgoing ?? 0;
		let openingBalance = sourcePoint?.openingBalance;
		const selectedScenario =
			scenarioId !== BASE_ID
				? scenarios.find((scenario) => scenario.id === scenarioId)
				: undefined;

		if (scenarioId !== BASE_ID) {
			const scenarioPoints = isLive
				? scenarioForecastData[scenarioId]?.projectedForecast
				: selectedScenario
					? applyScenarioToForecast(liveBaseData, selectedScenario.changes)
							.projectedForecast
					: undefined;
			const scenarioPoint = scenarioPoints?.[dayIndex];
			incoming = scenarioPoint?.scenarioIncoming ?? incoming;
			outgoing = scenarioPoint?.scenarioOutgoing ?? outgoing;
			openingBalance =
				(dayIndex > 0
					? scenarioPoints?.[dayIndex - 1]?.scenarioBalance
					: sourcePoint?.openingBalance) ??
				(balance - incoming + outgoing);
		}

		const scenarioName = selectedScenario?.name;

		const pointDate = new Date(point.date);
		const dateStr = format(pointDate, "MMMM dd, yyyy");

		// In live mode, filter forecast run transactions for this specific day
		let actualTransactions: DateBreakdownTransaction[] | undefined;
		if (isLive && runTxnError) {
			actualTransactions = [];
		} else if (isLive && forecastRunTransactions) {
			const dayStr = format(pointDate, "yyyy-MM-dd");
			actualTransactions = forecastRunTransactions
				.filter((txn) => format(new Date(txn.forecastDate), "yyyy-MM-dd") === dayStr)
				.map((txn) => ({
					name: txn.counterpartyName ?? "Unknown",
					description: txn.description,
					grossValueIn: txn.direction === "inflow" ? txn.outstandingAmount : 0,
					grossValueOut: txn.direction === "outflow" ? txn.outstandingAmount : 0,
					category: txn.category,
					dataSourceName: txn.dataSourceName ?? undefined,
				}));
		} else if (isLive) {
			actualTransactions = [];
		}

		const drillDown = generateDateBreakdownDrillDown(
			dateStr,
			balance,
			incoming,
			outgoing,
			pointDate,
			scenarioName,
			actualTransactions,
		);
		if (isLive && runTxnError) {
			drillDown.context = [
				...(drillDown.context ?? []),
				{ label: "Transaction Load Error", value: runTxnError instanceof Error ? runTxnError.message : "Failed to load transactions" },
			];
		} else if (isLive && !forecastRunId) {
			drillDown.context = [
				...(drillDown.context ?? []),
				{ label: "Note", value: `Run a ${getForecastTerm(false)} refresh to enable transaction drill-down` },
			];
		} else if (isLive && !forecastRunTransactions) {
			drillDown.context = [
				...(drillDown.context ?? []),
				{ label: "Note", value: "Loading transactions\u2026" },
			];
		}

		// Present the breakdown as an accounting summary (Opening → totals →
		// Closing → Net Change) using terminology-aware in/out labels. This is
		// specific to the forecast-point drill-down, so we override the shared
		// breakdown here rather than in generateDateBreakdownDrillDown (also used
		// by the manager ForecastTimelineChart).
		const movementTerms = getCashMovementTerms();
		openingBalance ??= balance - incoming + outgoing;
		drillDown.breakdown = [
			{ label: "Opening Balance", value: openingBalance, emphasis: "normal" },
			{ label: `Total ${movementTerms.in}`, value: incoming, emphasis: "normal" },
			{ label: `Total ${movementTerms.out}`, value: -outgoing, emphasis: "normal" },
			{
				label: "Closing Balance",
				value: balance,
				emphasis: "total",
				rule: "single",
			},
			{
				label: "Net Change",
				value: incoming - outgoing,
				emphasis: "total",
				rule: "double",
			},
		];
		// Hide the "Additional Information" section; keep Contributing Data Sources.
		drillDown.context = undefined;
		// Build the modal actions. Both are optional and depend on the host page:
		//  - "View Transactions": for a scenario overlay point only a
		//    caller-supplied `onViewTransactions` makes sense (it must target that
		//    scenario's own transactions table, not the base forecast), so the
		//    built-in base-table fallback is used for base-forecast points only.
		//    If no suitable target exists, the action is omitted.
		//  - "View Scenario": only for scenario overlay points, and only when
		//    `enableViewScenario` is set (hidden when already on a scenario page).
		const actions: NonNullable<typeof drillDown.actions> = [];
		const isScenarioPoint = !!scenarioName && scenarioId !== BASE_ID;
		const viewTransactions = isScenarioPoint
			? onViewTransactions
			: (showBaseTransactionsTable
				? (date: Date) => setTxnDateFilter(date)
				: undefined);
		if (viewTransactions) {
			actions.push({
				label: "View Transactions",
				variant: "primary",
				onClick: () => {
					closeDrillDown();
					viewTransactions(pointDate);
				},
			});
		}
		if (isScenarioPoint && enableViewScenario) {
			actions.push({
				label: "View Scenario",
				variant: "primary",
				onClick: () => {
					closeDrillDown();
					handleNavigate("scenarios", { scenarioId });
				},
			});
		}
		drillDown.actions = actions.length > 0 ? actions : undefined;
		showDrillDown("forecast-point", drillDown);
	};

	if (!hasPermission('view_90day_forecast_screen')) return null;

	if (isLive && forecastNotReady) {
		return (
			<div className="p-6">
				<Card className="border-dashed">
					<CardContent className="flex flex-col items-center justify-center py-16 text-center">
						<TrendingUp className="h-12 w-12 text-muted-foreground/40 mb-4" />
						<h3 className="text-lg font-semibold mb-2">
							No {getForecastTerm(false)} data available
						</h3>
						<p className="text-muted-foreground text-sm max-w-md mb-6">
							A {getForecastTerm(false)} refresh has not been run yet. Run a{" "}
							{getForecastTerm(false)} refresh to populate this screen with cash
							position data and projections.
						</p>
						{hasPermission("trigger_forecast_refresh") && (
							<Button onClick={() => handleNavigate("forecast-refresh")}>
								<RefreshCw className="h-4 w-4 mr-2" />
								Go to Refresh {getForecastTerm(true)}
							</Button>
						)}
					</CardContent>
				</Card>
			</div>
		);
	}

	const CustomTooltip = ({
		active,
		payload,
	}: {
		active?: boolean;
		payload?: any[];
	}) => {
		if (active && payload && payload.length) {
			return (
				<div className="bg-popover text-popover-foreground border shadow-md rounded-md px-3 py-2 text-sm">
					<p className="font-semibold mb-1.5">
						{(payload[0]?.payload as any)?.dateStr}
					</p>
					{payload.filter((e: any) => e.type !== 'none').map((entry: any, index: number) => (
						<div key={index} className="flex items-center gap-2">
							<div
								className="w-2.5 h-2.5 rounded-full flex-shrink-0"
								style={{ backgroundColor: entry.color }}
							/>
							<span className="text-muted-foreground">{entry.name}:</span>
							<span className="font-semibold ml-auto pl-3">
								{formatCurrency(entry.value)}
							</span>
						</div>
					))}
				</div>
			);
		}
		return null;
	};

	// ── Metric card computations ────────────────────────────────────────────────
	// Derived from the SAME dataset the chart plots: filteredData[BASE_ID].
	// Each balance is reduced to whole pennies (integer cents) so days that render
	// the same currency value are treated as equal. Extremes then report the FIRST
	// day the displayed value occurs, rather than an arbitrary day (down to a
	// fraction of a penny) inside a visually flat region.
	const baseCents = filteredData.map((d) =>
		Math.round(((d[BASE_ID] as number) ?? 0) * 100),
	);
	const hasBaseValues = baseCents.length > 0;
	const lowestCents = hasBaseValues ? Math.min(...baseCents) : 0;
	const highestCents = hasBaseValues ? Math.max(...baseCents) : 0;
	const lowestPoint = lowestCents / 100;
	const highestPoint = highestCents / 100;
	const lowestDay = hasBaseValues ? baseCents.indexOf(lowestCents) : -1;
	const highestDay = hasBaseValues ? baseCents.indexOf(highestCents) : -1;
	const averageBalance = hasBaseValues
		? Math.round(baseCents.reduce((sum, c) => sum + c, 0) / baseCents.length) / 100
		: 0;

	return (
		<div className="p-6">
			<DrillDownModal
				isOpen={isOpen}
				onClose={closeDrillDown}
				data={drillDownData}
			/>

			{/* ── Metric cards ─────────────────────────────────────────────────────── */}
			{!hideMetricCards && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">

				{/* Lowest Point */}
				<Card className="h-full" style={{ borderLeft: `3px solid ${lowestPoint < minCashThreshold ? 'var(--mc-error-border)' : 'var(--mc-warning-border)'}` }}>
					<CardContent className="p-4 flex flex-col justify-between h-full gap-4">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">Lowest Point</span>
							<div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: lowestPoint < minCashThreshold ? 'var(--mc-error-bg)' : 'var(--mc-warning-bg)' }}>
								<AlertTriangle className="h-4 w-4" style={{ color: lowestPoint < minCashThreshold ? 'var(--mc-error-icon)' : 'var(--mc-warning-icon)' }} />
							</div>
						</div>
						<div>
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">{hasBaseValues ? formatCurrency(lowestPoint, 'full') : '—'}</div>
							<div className="text-xs text-neutral-500 mt-1">
								{hasBaseValues ? `${lowestPoint < minCashThreshold ? '⚠ Below threshold — ' : ''}Day ${lowestDay}` : 'No data'}
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Average Balance */}
				<Card className="h-full" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
					<CardContent className="p-4 flex flex-col justify-between h-full gap-4">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">Average Balance</span>
							<div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: 'var(--mc-info-bg)' }}>
								<Calendar className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
							</div>
						</div>
						<div>
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">{hasBaseValues ? formatCurrency(averageBalance, 'full') : '—'}</div>
							<div className="text-xs text-neutral-500 mt-1">Next {getDaysCount()} days (base)</div>
						</div>
					</CardContent>
				</Card>

				{/* Highest Point */}
				<Card className="h-full" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
					<CardContent className="p-4 flex flex-col justify-between h-full gap-4">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">Highest Point</span>
							<div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: 'var(--mc-info-bg)' }}>
								<TrendingUp className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
							</div>
						</div>
						<div>
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">{hasBaseValues ? formatCurrency(highestPoint, 'full') : '—'}</div>
							<div className="text-xs text-neutral-500 mt-1">{hasBaseValues ? `Day ${highestDay}` : 'No data'}</div>
						</div>
					</CardContent>
				</Card>

			</div>}

			<Card>
				<div className="flex items-center justify-between flex-wrap gap-4 px-4 py-3 border-b">
					<div className="flex items-center gap-1">
						<CardTitle className="text-lg font-semibold text-neutral-900">
							{isScenarioEmbed ? `Cash Flow ${getScenarioTerm(true)} Overlay` : `Cash Flow ${getForecastTerm(true)}`}
						</CardTitle>
						<InfoTooltip
							title={isScenarioEmbed ? `Cash Flow ${getScenarioTerm(true)} Overlay` : `Cash Flow ${getForecastTerm(true)}`}
							content={isScenarioEmbed
								? `Your base ${getForecastTerm(false)} overlaid with this ${getScenarioTerm(false)}'s adjustments. Shows how your cash position would change if this ${getScenarioTerm(false)} plays out.`
								: "Your projected cash position over the selected period, based on expected income and outgoings from all connected data sources. Click any point on the chart to drill into that day's transactions."
							}
						/>
					</div>
					<div className="flex items-center gap-4">
						<Tabs
							value={timeRange}
							onValueChange={(v) => setTimeRange(v as TimeRange)}
						>
							<TabsList>
								<TabsTrigger value="7">7 Days</TabsTrigger>
								<TabsTrigger value="30">30 Days</TabsTrigger>
								<TabsTrigger value="90">90 Days</TabsTrigger>
							</TabsList>
						</Tabs>
						{!isScenarioEmbed && (
							<>
								<ScenarioOverlayPicker
									scenarios={scenarios}
									selectedIds={selectedIds}
									onToggle={toggleScenario}
								/>
								{selectedIds.filter(id => id !== BASE_ID).length > 0 && (
									<button
										type="button"
										onClick={() => setSelectedIds([BASE_ID])}
										className="text-xs text-muted-foreground hover:text-destructive transition-colors whitespace-nowrap"
									>
										Deselect all
									</button>
								)}
							</>
						)}
					</div>
				</div>

				{/* ── Active overlay pills — single scrollable row with arrow controls ── */}
				{selectedIds.filter(id => id !== BASE_ID).length > 0 && (
					<>
					<div className="relative border-b flex items-center">
						{/* Left arrow */}
						{canScrollLeft && (
							<button
								type="button"
								onClick={() => scrollPills('left')}
								className="flex-shrink-0 flex items-center justify-center w-7 h-full pl-2 text-muted-foreground hover:text-foreground transition-colors z-10"
								aria-label="Scroll left"
							>
								<ChevronLeft className="h-4 w-4" />
							</button>
						)}

						{/* Fade hint — left */}
						{canScrollLeft && (
							<div className="pointer-events-none absolute left-7 top-0 h-full w-6 bg-gradient-to-r from-background to-transparent z-10" />
						)}

						{/* Scrollable pills */}
						<div
							ref={pillsScrollRef}
							onScroll={updateScrollState}
							className="flex gap-2 px-4 py-2 overflow-x-auto scrollbar-none flex-1"
						>
							{selectedIds.filter(id => id !== BASE_ID).map((id, idx) => {
								const sc = scenarios.find(s => s.id === id);
								const label = sc ? sc.name : id;
								const colour = SCENARIO_COLOURS[(idx + 1) % SCENARIO_COLOURS.length];
								return (
									<Badge
										key={id}
										variant="outline"
										className={`gap-1.5 flex-shrink-0 ${isScenarioEmbed ? 'cursor-default' : 'cursor-pointer'}`}
										onClick={() => !isScenarioEmbed && toggleScenario(id)}
										style={{ borderColor: colour, color: colour }}
									>
										<span
											className="h-2 w-2 rounded-full flex-shrink-0"
											style={{ backgroundColor: colour }}
										/>
										{label}
										{!isScenarioEmbed && <span className="opacity-60">×</span>}
									</Badge>
								);
							})}
						</div>

						{/* Fade hint — right */}
						{canScrollRight && (
							<div className="pointer-events-none absolute right-7 top-0 h-full w-6 bg-gradient-to-l from-background to-transparent z-10" />
						)}

						{/* Right arrow */}
						{canScrollRight && (
							<button
								type="button"
								onClick={() => scrollPills('right')}
								className="flex-shrink-0 flex items-center justify-center w-7 h-full pr-2 text-muted-foreground hover:text-foreground transition-colors z-10"
								aria-label="Scroll right"
							>
								<ChevronRight className="h-4 w-4" />
							</button>
						)}
					</div>

					</>
				)}

				<CardContent className="pt-4">


					{/* biome-ignore lint/a11y/useKeyWithClickEvents: chart background dismiss */}
					<div
						ref={chartContainerRef}
						className="relative h-[400px] w-full outline-none"
						style={{ cursor: 'pointer' }}
						onMouseDown={(e) => {
							// Dismiss picker only when clicking chart background AND no modal is open
							if (clusterStateRef.current && !isOpen && (e.target as Element).closest('svg')) {
								setCluster(null);
							}
						}}
					>
						{clusterState && (
							<ClusterPickerPopover
								candidates={clusterState.candidates}
								periodLabel={clusterState.periodLabel}
								anchorX={clusterState.anchorX}
								anchorY={clusterState.anchorY}
								containerRef={chartContainerRef}
								onSelect={(id) => {
									triggerDrillDown(clusterState.point, id);
									setCluster(null);
								}}
								onDismiss={() => setCluster(null)}
							/>
						)}
						<ResponsiveContainer width="100%" height="100%">
							<ComposedChart
								data={filteredData}
								onClick={handlePointClick}
								onMouseMove={handleChartMouseMove}
								margin={{ top: 10, right: 30, left: 0, bottom: 0 }}

							>
								<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
								<XAxis
									dataKey="dateStr"
									tick={{ fontSize: 12 }}
									interval={Math.floor(getDaysCount() / 6)}
								/>
								<YAxis
									tick={{ fontSize: 12 }}
									tickFormatter={(value) =>
										`${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`
									}
								/>
								<Tooltip content={clusterStateRef.current ? () => null : <CustomTooltip />} />

								{/* Health zone backgrounds — only when bands mode */}
								{showBands && (
									<>
										<ReferenceArea y1={healthyThreshold} y2={10000000} fill="#dcfce7" fillOpacity={1} ifOverflow="hidden" />
										<ReferenceArea y1={cautionThreshold} y2={healthyThreshold} fill="#fef3c7" fillOpacity={1} ifOverflow="hidden" />
										<ReferenceArea y1={-10000000} y2={cautionThreshold} fill="#fee2e2" fillOpacity={1} ifOverflow="hidden" />
									</>
								)}

								{/* Threshold line — shown for bands and line modes */}
								{showLine && (
									<ReferenceLine
										y={minCashThreshold}
										stroke={CHART_THRESHOLD_COLOR}
										strokeDasharray="5 5"
										strokeWidth={2}
										label={{
											value: 'Threshold',
											position: 'insideTopLeft',
											fill: CHART_THRESHOLD_COLOR,
											fontSize: 12,
										}}
									/>
								)}

								{/* Lines — base uses dashboard sky colour, scenarios use their palette colour */}
								{selectedIds.map((id, idx) => (
									<Line
										key={`line-${id}`}
										type="monotone"
										dataKey={id}
										stroke={id === BASE_ID ? CHART_LINE_COLOR : colourFor(id, idx)}
										strokeWidth={id === BASE_ID ? 3 : 2}
										name={labelFor(id)}
										connectNulls={false}
										dot={false}
										activeDot={{
											r: 5,
											fill: id === BASE_ID ? CHART_LINE_COLOR : colourFor(id, idx),
											cursor: 'pointer',
										}}
									/>
								))}
							</ComposedChart>
						</ResponsiveContainer>
					</div>

					{/* Combined legend — below chart, centred */}
					<div className="mt-3 flex flex-wrap items-center justify-center gap-6 text-sm">
						{/* Base forecast line — always shown */}
						<div className="flex items-center gap-2">
							<div className="w-8 h-0.5 rounded-full" style={{ backgroundColor: CHART_LINE_COLOR }} />
							<span>Base {getForecastTerm(true)}</span>
						</div>
						{/* Threshold dashed line — shown for line/bands modes */}
						{showLine && (
							<div className="flex items-center gap-2">
								<div className="w-8 h-0 border-t-2 border-dashed" style={{ borderColor: CHART_THRESHOLD_COLOR }} />
								<span>Threshold ({getCurrencySymbol()}{(minCashThreshold / 1000).toFixed(0)}K)</span>
							</div>
						)}
						{/* Divider + zone legend — only for bands mode */}
						{showBands && (
							<>
								<div className="w-px h-4 bg-border" />
								<div className="flex items-center gap-2">
									<div className="w-3 h-3 rounded" style={{ backgroundColor: "#dcfce7", border: "1px solid var(--mc-success-icon)" }} />
									<span>Healthy (&gt;{getCurrencySymbol()}{(healthyThreshold / 1000).toFixed(0)}K)</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-3 h-3 rounded" style={{ backgroundColor: "#fef3c7", border: "1px solid var(--mc-warning-icon)" }} />
									<span>Caution ({getCurrencySymbol()}{(cautionThreshold / 1000).toFixed(0)}K–{getCurrencySymbol()}{(healthyThreshold / 1000).toFixed(0)}K)</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-3 h-3 rounded" style={{ backgroundColor: "#fee2e2", border: "1px solid var(--mc-error-icon)" }} />
									<span>Critical (&lt;{getCurrencySymbol()}{(cautionThreshold / 1000).toFixed(0)}K)</span>
								</div>
							</>
						)}
					</div>

				</CardContent>
			</Card>

			{/* Forecast Transactions table — live run-based data or in-memory demo data (hidden in scenario-embed mode) */}
			{showBaseTransactionsTable && (
				<ForecastTransactionsTable dateFilter={txnDateFilter} />
			)}
		</div>
	);
};
